import Backbone from 'backbone';
import Promise from 'bluebird';
import $ from 'jquery';
import _ from 'underscore';
import { AccountsMode } from '@bingads-webui-campaign/overview-cards-common';
import { CampaignType as AccountLevelCampaignType } from '@bingads-webui-campaign/campaign-ui-common';

import { dashboardConfig } from '../config/dashboard-config';
import { welcomeCardConfig } from '../config/welcome-card-config';
import { getQuickInstrumentation } from '../instrumentation/log-helper';

const CampaignLevelCampaignType = _.mapObject(AccountLevelCampaignType, (val, key) => key);

function uri() {
  const api = 'Customers(:advertisercustomer_id)/Accounts(:account_id)/Default.AlertInformation';
  const odataParam = '$select=HasBIData,HasCampaigns,HasOtherAccounts';

  return `${api}?${odataParam}`;
}

export class CardState extends Backbone.Model {
  initialize({
    navigationLevel,
    permissions,
    edm,
    pageContext,
    odata,
    preferences,
    instrumentationSettings,
    instrumentation,
    constants,
    newCardsConfig = null,
    welcomeCardsConfig,
    accountsMode = AccountsMode.Expert,
    campaignType,
    isPrefetch = false,
  }) {
    this.isPrefetch = isPrefetch;
    if (navigationLevel === 'customer') {
      // eslint-disable-next-line no-param-reassign
      edm.customerIdType.useAdvertiserCustomerId = false;
    }
    this.set({
      name: 'default',
      navigationLevel,
    });
    this.campaignType = campaignType;
    this.pageContext = pageContext;
    this.odata = odata;
    this.newCardsConfig = newCardsConfig;
    this.welcomeCardsConfig = welcomeCardsConfig;
    this.preferences = preferences;
    this.accountsMode = accountsMode;
    // Get the ID based on the name and navigation level before anything else.
    this.p$Initialized = preferences.initialize().then(() => {
      this.fetchDashboardId();
    });

    this.cardsConfig = newCardsConfig({
      permissions,
      edm,
      accountsMode,
    });

    this.quickInstrumentation = this.isPrefetch ? _.noop :
      getQuickInstrumentation({
        instrumentationSettings,
        instrumentation,
        constants,
      });
  }

  fetchDashboardId() {
    const name = this.get('name');

    if (this.accountsMode === AccountsMode.Smart) {
      this.set('dashboardId', `${name}-${this.navigationLevel}-smart`);
    } else if (this.campaignType === CampaignLevelCampaignType.Hotel || this.campaignType === AccountLevelCampaignType.Hotel) {
      this.set('dashboardId', `${name}-${this.navigationLevel}-hotel`);
    } else if (this.campaignType === CampaignLevelCampaignType.PerformanceMax || this.campaignType === AccountLevelCampaignType.PerformanceMax) {
      this.set('dashboardId', `${name}-${this.navigationLevel}-performancemax`);
    } else if (this.campaignType === CampaignLevelCampaignType.App || this.campaignType === AccountLevelCampaignType.App) {
      this.set('dashboardId', `${name}-${this.navigationLevel}-app`);
    } else {
      this.set('dashboardId', `${name}-${this.navigationLevel}`);
    }
  }

  loadDashboardState() {
    // get all dashboard preference
    this.prefs = this.getDashBoardPreferences();
    if (this.prefs && _.has(this.prefs.Data, this.dashboardId) && _.has(this.prefs.Data[this.dashboardId], 'options')) {
      this.set('options', _.extend(_.omit(this.prefs.Data[this.dashboardId].options, ['reOrderedForV2', 'showWelcomeTips', 'reOrderedForABCTest']), this.options));
    }

    const userWelcomePref = this.preferences.findByNameAtUserLevel(this.get('userWelcomePrefName'));
    const doneWithWelcomeTips = userWelcomePref ? userWelcomePref.Data : false;
    const doNotShowWelcomeTips = this.dashboardId !== 'default-account' || doneWithWelcomeTips === true;
    const welcomeStatus = new Promise((resolve) => {
      if (doNotShowWelcomeTips) {
        resolve({ showWelcomeTips: false });
      } else if (this.pageContext.WelcomeStatus &&
        !this.pageContext.WelcomeStatus.doneInitialLoad) {
        resolve({
          showWelcomeTips: this.pageContext.WelcomeStatus.ShowWelcomeTips,
          createdCampaign: this.pageContext.WelcomeStatus.HasCampaigns,
          skippedBothAtAccountCreation: this.options.skippedBothAtAccountCreation,
          skippedNoneAtAccountCreation: this.options.skippedNoneAtAccountCreation,
        });
        this.pageContext.WelcomeStatus.doneInitialLoad = true;
      } else {
        this.odata.get(uri()).then((data) => {
          if (data.HasBIData || data.HasOtherAccounts) {
            resolve({ showWelcomeTips: false });
          } else {
            resolve({
              showWelcomeTips: true,
              createdCampaign: data.HasCampaigns,
              skippedBothAtAccountCreation: this.options.skippedBothAtAccountCreation,
              skippedNoneAtAccountCreation: this.options.skippedNoneAtAccountCreation,
            });
          }
        }).catch(() => {
          resolve({ showWelcomeTips: false });
        });
      }
    });

    return welcomeStatus.then((status) => {
      if (status.showWelcomeTips) {
        this.showWelcomeTips = true;
        this.loadWelcomeTips();
        this.welcomeAttr = _.clone(this.welcomeCardsConfig[0]);
        this.welcomeAttr.constructOptions = status;
        this.config = dashboardConfig.getWelcomeConfig();
        this.config.getContentConstructor = this.welcomeCardsConfig.getContentConstructor;
        // for the users we show welcome tips, set overview tab as default.
        this.preferences.set({
          Name: this.get('OverviewAsDefaultPrefName'),
          Data: true,
        });
      } else {
        this.showWelcomeTips = false;
        if (!doNotShowWelcomeTips) {
          // status changed. We will not show welcome tips for this user anymore.
          // option clean up
          const newOptions = _.omit(this.options, ['skippedBothAtAccountCreation', 'doneWithWelcomeTips', 'skippedNoneAtAccountCreation']);

          this.set('options', newOptions);
          _.defer(() => this.save({ newOptions }));
          this.quickInstrumentation('LoadOverviewPage', 'No welcomeTips for this user anymore.');
          this.preferences.setAtUserLevel({
            Name: this.get('userWelcomePrefName'),
            Data: true,
          });
        }
        this.config = dashboardConfig.getDefaultConfig();
        this.config.getContentConstructor = this.cardsConfig.getContentConstructor;
        this.loadDashBoardCards();
      }
    });
  }

  loadWelcomeTips() {
    this.set('cards', _.map(welcomeCardConfig, card => ({
      id: card.typeId,
      sizeOption: card.sizeOption,
    })));
  }

  loadDashBoardCards() {
    if (this.prefs && _.has(this.prefs.Data, this.dashboardId) && _.has(this.prefs.Data[this.dashboardId], 'cards')) {
      const cardGroup = _.groupBy(
        this.cardsConfig,
        cardConfig => this.getCardCategory(cardConfig)
      );

      let availableCards = cardGroup.defaultIn;
      let cards;
      let savePreference = false;

      if (_.size(cardGroup.defaultOut) !== 0) {
        availableCards = availableCards.concat(cardGroup.defaultOut);
      }

      // See if we have any retired cards:
      // cards that in the user's preference but not available anymore.
      const retiredCards = _.difference(_.map(this.prefs.Data[this.dashboardId].cards, card =>
        card.id), _.map(availableCards, card =>
        card.typeId));

      if (_.size(retiredCards) !== 0) {
        this.quickInstrumentation('RemoveRetiredTilesFromOverview');
      }
      // Filter out retired cards from preference.
      this.prefs.Data[this.dashboardId].cards = _.filter(
        this.prefs.Data[this.dashboardId].cards,
        card => !_.contains(retiredCards, card.id)
      );
      // Filter out hidden cards.
      cards = _.filter(this.prefs.Data[this.dashboardId].cards, card => !card.hidden);

      // Check for cards that are not in users preference -- new cards for the user.
      const newDefaultInCards = _.difference(_.map(cardGroup.defaultIn, card =>
        card.typeId), _.map(this.prefs.Data[this.dashboardId].cards, card =>
        card.id));

      const newDefaultOutCards = _.difference(_.map(cardGroup.defaultOut, card =>
        card.typeId), _.map(this.prefs.Data[this.dashboardId].cards, card =>
        card.id));

      if (_.size(newDefaultOutCards) !== 0) {
        this.set('newDefaultOutCardsAvailable', true);
        this.quickInstrumentation('AddNewTilesToOverview', `Adding new default out cards: ${newDefaultOutCards.join(', ')}`);
      }
      // Add new default-in cards to the collection.
      if (_.size(newDefaultInCards) !== 0) {
        const newCardConfig = _.chain(cardGroup.defaultIn)
          .filter(card => _.indexOf(newDefaultInCards, card.typeId) !== -1)
          .map(card => ({
            id: card.typeId,
            sizeOption: card.sizeOption,
          })).value();

        cards = cards.concat(newCardConfig);
        this.set('newDefaultInCards', newDefaultInCards);
        this.quickInstrumentation('AddNewTilesToOverview', `Adding new default in cards: ${newDefaultInCards.join(', ')}`);
      }

      _.each(cards, (card) => {
        if (card.sizeOption) {
          // If user does not have pref for some size due to new size options. use the one in config
          if (_.size(this.config.layoutOptions) !== _.size(card.sizeOption)) {
            const cardConfig = _.find(this.cardsConfig, item => item.typeId === card.id);

            _.each(_.keys(cardConfig.sizeOption), (key) => {
              if (!card.sizeOption[key]) {
                // eslint-disable-next-line no-param-reassign
                card.sizeOption[key] = { width: cardConfig.sizeOption[key].width };
                savePreference = true;
              }
            });
          }
        } else {
          // Some old preferences might not contain sizeOptions. Add it if it's missing.
          const cardConfig = _.find(this.cardsConfig, item => item.typeId === card.id);

          // eslint-disable-next-line no-param-reassign
          card.sizeOption = cardConfig.sizeOption;
          savePreference = true;
        }
      });

      // Update preference if its modified due to new cards or retirement.
      savePreference = savePreference ||
        _.size(retiredCards) !== 0 ||
        _.size(newDefaultInCards) !== 0;

      this.set('cards', cards);
      if (savePreference) {
        _.defer(() => this.save({
          newCards: cards,
          newOptions: this.options,
        }));
      }
    } else {
      this.quickInstrumentation('FirstUse');
      // Add all the default-in cards.
      const cardGroup = _.groupBy(
        this.cardsConfig,
        cardConfig => this.getCardCategory(cardConfig)
      );

      const defaultCards = _.map(cardGroup.defaultIn, card => ({
        id: card.typeId,
        sizeOption: card.sizeOption,
      }));

      this.set('cards', defaultCards);

      let newOptions;

      _.defer(() => this.save({
        newCards: defaultCards,
        newOptions,
      }));
    }
  }

  sync() {
    return this.p$Initialized.then(() => this.loadDashboardState());
  }

  save({
    newCards,
    newOptions,
  }) {
    if (this.isPrefetch || (_.isUndefined(newCards) && _.isUndefined(newOptions))) {
      return;
    }

    let cards;

    // Do not save card specific prefs for welcome tips.
    if (!this.showWelcomeTips) {
      if (newCards) {
        cards = newCards;
      } else {
        ({ cards } = this);
      }

      if (cards && this.prefs && _.has(this.prefs.Data, this.dashboardId) && _.has(this.prefs.Data[this.dashboardId], 'cards')) {
        const hiddenCards = _.difference(
          _.map(this.prefs.Data[this.dashboardId].cards, card => card.id),
          _.map(cards, card => card.id)
        );

        this.set('cards', cards);
        if (_.size(hiddenCards) !== 0) {
          cards = cards.concat(_.map(hiddenCards, card => ({ id: card, hidden: true })));
        }
      }
    }

    const options = _.extend({}, this.options, newOptions);

    if (!_.isUndefined(options)) {
      this.set('options', options);
    }

    let data = {};

    if (this.prefs) {
      data = _.extend(data, this.prefs.Data);
    }

    data = _.extend(data, {
      [this.dashboardId]: {
        cards,
        options,
      },
    });

    const preference = {
      Data: data,
    };

    this.setDashBoardPreferences(preference);

    if (this.prefs) {
      this.prefs.Data = data;
    } else {
      this.prefs = preference;
    }
  }

  getCardAttrByTypeId(id) {
    let card;

    if (this.showWelcomeTips) {
      card = this.welcomeAttr;
    } else {
      card = _.find(this.cardsConfig, conf => conf.typeId === id &&
        this.cardsConfig.isValidForNavigationLevel(conf, this.navigationLevel, this.campaignType));
    }

    if (_.isUndefined(card)) {
      return null;
    }
    const attribute = {};

    // create a deep copy before return
    $.extend(true, attribute, _.pick(
      card,
      ['titleKey', 'addCardTitleKey', 'name', 'contentType', 'constructOptions', 'sizeOption', 'preDefinedMenuItems', 'useNewI18n']
    ));

    // If we have size options stored in preference, over write with preference.
    const cardPref = _.find(this.cards, item => item.id === id);

    if (cardPref && cardPref.sizeOption) {
      _.each(cardPref.sizeOption, (value, key) => {
        if (attribute.sizeOption[key]) {
          attribute.sizeOption[key].width = value.width;
        }
      });
    }

    return attribute;
  }

  getCardCategory(cardConfig) {
    if (!this.cardsConfig.isValidForNavigationLevel(
      cardConfig,
      this.navigationLevel,
      this.campaignType
    )) {
      return 'notEnabledOrValidForNavigationLevel';
    } else if (cardConfig.defaultIn) {
      return 'defaultIn';
    }
    return 'defaultOut';
  }

  getDashBoardPreferences() {
    if (this.navigationLevel === 'customer') {
      return this.preferences.findByNameAtUserLevel(this.get('accountsDashboardPrefName'));
    }

    return this.preferences.findByNameAtAccountLevel(this.get('dashboardPrefName'));
  }

  setDashBoardPreferences(preference) {
    if (this.navigationLevel === 'customer') {
      this.preferences.setAtUserLevel(_.extend({}, preference, { Name: this.get('accountsDashboardPrefName') }));
    } else {
      this.preferences.setAtAccountLevel(_.extend({}, preference, { Name: this.get('dashboardPrefName') }));
    }
  }

  // Getters for the caller's convenience
  get cards() {
    return this.get('cards');
  }

  get options() {
    return this.get('options');
  }

  get dashboardId() {
    return this.get('dashboardId');
  }

  get navigationLevel() {
    return this.get('navigationLevel');
  }

  get newDefaultOutCardsAvailable() {
    return this.get('newDefaultOutCardsAvailable');
  }

  get newDefaultInCards() {
    return this.get('newDefaultInCards');
  }

  get defaults() {
    return {
      dashboardPrefName: 'dashboard/preferences',
      accountsDashboardPrefName: 'accountsDashboard/preferences',
      userWelcomePrefName: 'doneWithWelcomeTips',
      OverviewAsDefaultPrefName: 'OverviewAsDefaultTab',
      name: '',
      navigationLevel: 'account',
      dashboardId: 0,
      cards: [],
      options: {},
      newDefaultOutCardsAvailable: false,
    };
  }
}
